if (!customElements.get('byob-builder')) {
  // Dynamic discount tiers: { itemCount: percentOff }. Single source of truth
  // for both the live tray math and the Flex Bundles cart-add payload.
  const BYOB_DISCOUNT_TIERS = { 2: 10, 3: 15, 4: 20, 5: 25, 6: 30 };

  customElements.define(
    'byob-builder',
    class extends HTMLElement {
      connectedCallback() {
        this.minItems = parseInt(this.dataset.minItems, 10) || 2;
        this.maxItems = parseInt(this.dataset.maxItems, 10) || 6;
        this.parentVariantId = parseInt(this.dataset.parentVariantId, 10);
        this.parentTitle = this.dataset.parentTitle || '';
        this.bundleName = this.dataset.bundleName || this.parentTitle || 'Bundle';
        this.currency = this.dataset.currency || 'USD';

        this.tiers = BYOB_DISCOUNT_TIERS;
        this.selected = [];
        this.prevCount = 0;
        this.celebrateEmojis = Array.from(this.dataset.celebrateEmojis || '🎉✨🥳');

        // Gift with purchase (optional). Disabled when no variant id is set.
        this.gwp = this.dataset.gwpVariantId
          ? {
              variantId: parseInt(this.dataset.gwpVariantId, 10),
              title: this.dataset.gwpTitle || '',
              image: this.dataset.gwpImage || '',
              threshold: parseInt(this.dataset.gwpThreshold, 10) || this.maxItems,
              label: this.dataset.gwpLabel || 'Free gift included',
              pendingLabel: this.dataset.gwpPendingLabel || 'Add {count} more for a free gift',
            }
          : null;
        this.gwpEl = this.querySelector('[data-byob-gwp]');
        this.gwpLabelEl = this.querySelector('[data-byob-gwp-label]');

        const stringsNode = this.querySelector('script[data-byob-strings]');
        this.strings = stringsNode ? JSON.parse(stringsNode.textContent) : {};

        this.slotsEl = this.querySelector('[data-byob-slots]');
        this.progressBarEl = this.querySelector('[data-byob-progress-bar]');
        this.progressTickEls = Array.from(this.querySelectorAll('.byob__progress-tick'));
        this.statusEl = this.querySelector('[data-byob-status]');
        this.countEl = this.querySelector('[data-byob-count]');
        this.subtotalEl = this.querySelector('[data-byob-subtotal]');
        this.savingsRowEl = this.querySelector('[data-byob-savings-row]');
        this.savingsEl = this.querySelector('[data-byob-savings]');
        this.totalEl = this.querySelector('[data-byob-total]');
        this.ctaEl = this.querySelector('[data-byob-add]');
        this.ctaLabelEl = this.querySelector('[data-byob-cta-label]');
        this.ctaSpinnerEl = this.ctaEl && this.ctaEl.querySelector('.loading__spinner');

        this.productBtns = Array.from(this.querySelectorAll('[data-byob-product]'));
        this.productBtns.forEach((btn) => {
          btn.addEventListener('click', () => this.addItem(btn));
        });

        this.slotsEl.addEventListener('click', (e) => {
          const removeBtn = e.target.closest('[data-byob-remove]');
          if (!removeBtn) return;
          const idx = parseInt(removeBtn.dataset.byobRemove, 10);
          this.removeItem(idx);
        });

        this.ctaEl.addEventListener('click', () => {
          if (this.selected.length >= this.minItems) this.submitBundle();
        });

        // Pin the pill nav's "stuck" styling only once it's actually sticky.
        // Compare the nav's bounding-box top to its current sticky `top` value
        // (which CSS swaps between 0 and var(--header-height) depending on
        // whether the global header is pinned). When they match, it's stuck.
        this.navEl = this.querySelector('.byob__nav');
        if (this.navEl) {
          const checkStuck = () => {
            const targetTop = parseFloat(getComputedStyle(this.navEl).top) || 0;
            const actualTop = this.navEl.getBoundingClientRect().top;
            this.navEl.classList.toggle('byob__nav--stuck', actualTop <= targetTop + 0.5);
          };
          window.addEventListener('scroll', checkStuck, { passive: true });
          window.addEventListener('resize', checkStuck);
          requestAnimationFrame(checkStuck);
        }

        this.render();
      }

      addItem(btn) {
        if (this.selected.length >= this.maxItems) return;
        this.selected.push({
          variantId: parseInt(btn.dataset.variantId, 10),
          productId: parseInt(btn.dataset.productId, 10),
          title: btn.dataset.title,
          price: parseInt(btn.dataset.price, 10) || 0,
          image: btn.dataset.image,
          url: btn.dataset.url,
        });
        this.render();
      }

      removeItem(idx) {
        this.selected.splice(idx, 1);
        this.render();
      }

      // Max-tier celebration: spawns floating emoji from the progress bar.
      // Mirrors the cart-shipping-countdown <milestone-celebrate> pattern.
      celebrate() {
        const anchor = this.progressBarEl?.parentElement;
        if (!anchor || !this.celebrateEmojis.length) return;
        const rect = anchor.getBoundingClientRect();
        const count = Math.max(8, this.celebrateEmojis.length * 3);
        for (let i = 0; i < count; i++) {
          const emoji = this.celebrateEmojis[i % this.celebrateEmojis.length];
          const el = document.createElement('span');
          el.className = 'byob-celebrate-emoji';
          el.textContent = emoji;
          el.style.left = rect.left + Math.random() * rect.width + 'px';
          el.style.top = rect.top + 'px';
          el.style.animationDelay = (i * 0.04) + 's';
          el.style.setProperty('--byob-drift', (Math.random() * 80 - 40) + 'px');
          document.body.appendChild(el);
          el.addEventListener('animationend', () => el.remove());
        }
        // Also pulse the tray so the moment lands beyond the floating bits.
        const trayInner = this.querySelector('[data-byob-tray] .byob__tray-inner') ||
          this.querySelector('.byob__tray-inner');
        if (trayInner) {
          trayInner.classList.remove('byob__tray-inner--celebrate');
          void trayInner.offsetWidth;
          trayInner.classList.add('byob__tray-inner--celebrate');
        }
      }

      t(key, vars) {
        let str = this.strings[key] || '';
        if (!vars) return str;
        Object.keys(vars).forEach((k) => {
          str = str.split('__' + k.toUpperCase() + '__').join(vars[k]);
        });
        return str;
      }

      currentDiscountPct() {
        const n = this.selected.length;
        return this.tiers[n] || 0;
      }

      subtotal() {
        return this.selected.reduce((s, i) => s + i.price, 0);
      }

      formatMoney(cents) {
        const noFraction = cents % 100 === 0;
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: noFraction ? 0 : 2,
            maximumFractionDigits: 2,
          }).format(cents / 100);
        } catch (_) {
          return '$' + (noFraction ? Math.round(cents / 100) : (cents / 100).toFixed(2));
        }
      }

      render() {
        const n = this.selected.length;
        const pct = this.currentDiscountPct();
        const subtotal = this.subtotal();
        const savings = Math.round((subtotal * pct) / 100);
        const total = subtotal - savings;

        // Celebrate when the customer first crosses into the max tier.
        if (n === this.maxItems && this.prevCount < this.maxItems) {
          this.celebrate();
        }
        this.prevCount = n;

        // GWP badge state — pending below threshold, unlocked at/above it.
        if (this.gwp && this.gwpEl) {
          const unlocked = n >= this.gwp.threshold;
          this.gwpEl.hidden = false;
          this.gwpEl.classList.toggle('byob__gwp--unlocked', unlocked);
          if (this.gwpLabelEl) {
            const remaining = this.gwp.threshold - n;
            this.gwpLabelEl.textContent = unlocked
              ? this.gwp.label
              : this.gwp.pendingLabel
                  .replace('[count]', remaining)
                  .replace('{count}', remaining); // back-compat for older saved values
          }
        }

        // Lock further selection at max. Skips natively-disabled (sold-out) items.
        const isFull = n >= this.maxItems;
        this.classList.toggle('byob--full', isFull);
        this.productBtns.forEach((btn) => {
          if (btn.hasAttribute('disabled')) return;
          btn.setAttribute('aria-disabled', isFull ? 'true' : 'false');
        });

        // Slots
        Array.from(this.slotsEl.children).forEach((slot, i) => {
          const item = this.selected[i];
          const wasFilled = slot.classList.contains('byob__slot--filled');
          if (item) {
            slot.classList.add('byob__slot--filled');
            slot.innerHTML = `
              <span class="byob__slot-media">
                ${item.image ? `<img src="${item.image}" alt="" loading="lazy" />` : ''}
              </span>
              <span class="byob__slot-info">
                <span class="byob__slot-title text-body-3">${item.title}</span>
                <span class="byob__slot-price text-body-3">${this.formatMoney(item.price)}</span>
              </span>
              <button type="button" class="byob__slot-remove" data-byob-remove="${i}" aria-label="${this.t('remove_item', { title: item.title })}">&times;</button>
            `;
            // Trigger entry animation only when this slot is newly filled.
            if (!wasFilled) {
              slot.classList.remove('byob__slot--enter');
              // Force reflow so the animation restarts.
              void slot.offsetWidth;
              slot.classList.add('byob__slot--enter');
            }
          } else {
            slot.classList.remove('byob__slot--filled', 'byob__slot--enter');
            slot.innerHTML = `<span class="byob__slot-empty" aria-hidden="true">${i + 1}</span>`;
          }
        });

        // Progress bar — fills across the tier ticks. The 5 ticks span 0%→100%
        // of the bar (tier 2 at 0%, tier 6 at 100%), so the fill maps the same:
        // n=2 → 0%, n=6 → 100%, anything below 2 → 0%.
        const fillPct = n < this.minItems
          ? 0
          : Math.min(100, ((n - this.minItems) / (this.maxItems - this.minItems)) * 100);
        this.progressBarEl.style.width = fillPct + '%';

        this.progressTickEls.forEach((tick) => {
          const tier = parseInt(tick.dataset.tier, 10);
          tick.classList.toggle('is-unlocked', n >= tier);
        });

        // Count
        this.countEl.textContent = this.t('count', { count: n, max: this.maxItems });

        // Status copy
        if (n < this.minItems) {
          this.statusEl.textContent = this.t('status_add_to_unlock', {
            count: this.minItems - n,
            pct: this.tiers[this.minItems],
          });
        } else if (n < this.maxItems) {
          const nextPct = this.tiers[n + 1];
          this.statusEl.textContent = nextPct
            ? this.t('status_tier_unlocked', { pct, next_pct: nextPct })
            : this.t('status_tier_final', { pct });
        } else {
          this.statusEl.textContent = this.t('status_max_unlocked', { pct });
        }

        // Totals — Total is the dominant number; subtotal shows as a struck-through
        // "was" price next to it once savings kick in.
        this.subtotalEl.textContent = subtotal > 0 ? this.formatMoney(subtotal) : '-';
        if (savings > 0) {
          this.savingsRowEl.hidden = false;
          this.savingsEl.textContent = '−' + this.formatMoney(savings);
          this.totalEl.innerHTML = `<s class="byob__total-was">${this.formatMoney(subtotal)}</s> <strong class="byob__total-now">${this.formatMoney(total)}</strong>`;
        } else {
          this.savingsRowEl.hidden = true;
          this.totalEl.innerHTML = `<strong class="byob__total-now">${this.formatMoney(total)}</strong>`;
        }

        // CTA
        if (n < this.minItems) {
          this.ctaEl.disabled = true;
          this.ctaLabelEl.textContent = this.t('cta_add_more', { count: this.minItems - n });
        } else {
          this.ctaEl.disabled = false;
          this.ctaLabelEl.textContent = this.t('cta_add_bundle', { total: this.formatMoney(total) });
        }
      }

      // Flex Bundles "Component Sum" mode: bundle total = sum of component
      // prices. Each component's `price` is the per-item discounted value
      // (cents → dollars), so the tier % is baked into what Flex charges.
      async submitBundle() {
        const selected = this.selected;
        const pct = this.currentDiscountPct();
        const factor = (100 - pct) / 100;
        const discountedPrice = (cents) => Number((((cents || 0) * factor) / 100).toFixed(2));

        const componentPayload = selected.map((s) => ({
          id: s.variantId,
          quantity: 1,
          price: discountedPrice(s.price),
        }));

        // Append GWP as a $0 component when its threshold is met.
        const gwpUnlocked = this.gwp && selected.length >= this.gwp.threshold;
        if (gwpUnlocked) {
          componentPayload.push({
            id: this.gwp.variantId,
            quantity: 1,
            price: 0,
          });
        }

        const properties = {
          _components: JSON.stringify(componentPayload),
          _settings: JSON.stringify({ title: this.bundleName }),
        };
        selected.forEach((s, idx) => {
          properties['Item ' + (idx + 1)] = s.title;
        });
        if (gwpUnlocked) {
          properties['Item ' + (selected.length + 1)] = this.gwp.title + ' (free gift)';
        }

        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.ctaEl.setAttribute('aria-disabled', 'true');
        this.ctaEl.classList.add('loading');
        if (this.ctaSpinnerEl) this.ctaSpinnerEl.classList.remove('hidden');
        if (cart && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(document.activeElement);
        }

        const sections =
          cart && typeof cart.getSectionsToRender === 'function'
            ? cart.getSectionsToRender().map((s) => s.id)
            : ['cart-drawer', 'cart-icon-bubble'];

        const body = {
          items: [{ id: this.parentVariantId, quantity: 1, properties }],
          sections,
          sections_url: window.location.pathname,
        };

        try {
          const res = await fetch(this.cartRoute('cart/add.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const result = await res.json();
          if (result && result.status && Number(result.status) >= 400) {
            throw new Error(result.description || 'Failed to add bundle');
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'byob-builder',
              productVariantId: this.parentVariantId,
              cartData: result,
            });
          }

          if (cart) {
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            if (typeof cart.renderContents === 'function') cart.renderContents(result);
          }

          this.selected = [];
          this.render();
        } catch (err) {
          console.error('[byob-builder] add failed', err);
        } finally {
          this.ctaEl.removeAttribute('aria-disabled');
          this.ctaEl.classList.remove('loading');
          if (this.ctaSpinnerEl) this.ctaSpinnerEl.classList.add('hidden');
        }
      }

      cartRoute(path) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        return root + path;
      }
    }
  );
}
